import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter } from 'react-router'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import App from './App.tsx'
import './styles/index.css'
import './i18n/config'
import { logger } from './lib/logger'
import { env } from './lib/env'
import { initAppInsights } from './lib/appInsights'
import { ThemeProvider } from './hooks/useTheme'
import { NotificationsProvider } from './context/NotificationsContext'
import { DriverProvider } from './contexts/DriverContext'
import { RecordingStatusProvider } from './contexts/RecordingStatusContext'
import { AuthSyncService } from './services/auth-sync-service'
import { applyBranding } from './lib/branding'

// Bootstrap Azure Application Insights as early as possible — before any
// component mounts or services kick off. No-op when
// VITE_APPINSIGHTS_CONNECTION_STRING is unset (local dev). See
// cdb_control_center#1 / docs (cdb_control_center/docs/OBSERVABILITY.md).
initAppInsights()

// One-time cleanup: the removed Supabase client persisted sessions under these
// localStorage keys — no code owns them anymore, so stale tokens would linger forever.
function removeLegacySupabaseAuthKeys() {
  localStorage.removeItem('cdb-webos-supabase-auth')
  localStorage.removeItem('cdb-webos-supabase-admin')
}
removeLegacySupabaseAuthKeys()

// Override console methods to respect debug mode (except i18n which always logs)
if (!env.debugMode) {
  const noop = () => {}
  const originalConsole = {
    log: console.log,
    info: console.info,
    warn: console.warn,
    error: console.error,
    debug: console.debug,
    group: console.group,
    groupCollapsed: console.groupCollapsed,
    groupEnd: console.groupEnd,
  }

  // Check if the call is from i18n files
  const isI18nCall = () => {
    const stack = new Error().stack || ''
    return stack.includes('/i18n/') || stack.includes('i18n')
  }

  console.log = (...args: any[]) => isI18nCall() ? originalConsole.log(...args) : noop()
  console.info = (...args: any[]) => isI18nCall() ? originalConsole.info(...args) : noop()
  console.warn = (...args: any[]) => isI18nCall() ? originalConsole.warn(...args) : noop()
  console.error = (...args: any[]) => isI18nCall() ? originalConsole.error(...args) : noop()
  console.debug = (...args: any[]) => isI18nCall() ? originalConsole.debug(...args) : noop()
  console.group = (...args: any[]) => isI18nCall() ? originalConsole.group(...args) : noop()
  console.groupCollapsed = (...args: any[]) => isI18nCall() ? originalConsole.groupCollapsed(...args) : noop()
  console.groupEnd = () => isI18nCall() ? originalConsole.groupEnd() : noop()
}

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 1000 * 60 * 5, // 5 minutes
    },
  },
})

// Apply client branding (tab title + favicons) before first paint
applyBranding()

// Initialize logger
logger.info({
  environment: import.meta.env.MODE,
  timestamp: new Date().toISOString(),
}, 'Application starting...')

// Initialize Auth Sync Service. Notifications are loaded inside onUserLogin();
// the NotificationsProvider additionally fetches on mount — a separate startup
// notification init would be a third, identical request pair.
AuthSyncService.initialize().catch(error => {
  logger.error({ error }, 'Fehler bei der Initialisierung des Auth Sync Service')
})

// Log unhandled errors. Pino chokes on `event.error === null` (calls
// .constructor on it), so coerce nullish errors to a string.
window.addEventListener('error', (event) => {
  logger.error({
    message: event.message,
    filename: event.filename,
    lineno: event.lineno,
    colno: event.colno,
    error: event.error ?? String(event.message ?? 'unknown'),
  }, 'Unhandled error')
})

// Log unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  logger.error({
    reason: event.reason,
    promise: event.promise,
  }, 'Unhandled promise rejection')
})

// Recover from stale lazy-chunk loads. Vite fires `vite:preloadError` when a
// dynamically-imported chunk fails to load — almost always because a deploy
// replaced the hashed asset filenames while this tab still holds the old
// index.html. Reload once to fetch the fresh index.html + current hashes.
//
// The sessionStorage guard prevents a tight reload loop when a chunk fails
// *during initial load* (a genuinely broken deploy never reaches the `load`
// event, so the flag is never cleared and the second attempt is blocked).
// On a successful load the flag is cleared, so a later staleness event
// (e.g. the next deploy, minutes later) gets its own fresh one-shot reload.
const PRELOAD_RELOAD_FLAG = 'vite-preload-reloaded'
window.addEventListener('vite:preloadError', () => {
  if (sessionStorage.getItem(PRELOAD_RELOAD_FLAG)) return
  sessionStorage.setItem(PRELOAD_RELOAD_FLAG, '1')
  logger.warn('vite:preloadError — stale chunk, reloading once for fresh assets')
  window.location.reload()
})
window.addEventListener('load', () => {
  sessionStorage.removeItem(PRELOAD_RELOAD_FLAG)
})

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider defaultTheme="system" storageKey="cdb-theme">
        <NotificationsProvider>
          <DriverProvider>
            <RecordingStatusProvider>
              <BrowserRouter>
                <App />
              </BrowserRouter>
            </RecordingStatusProvider>
          </DriverProvider>
        </NotificationsProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)

logger.info('Application rendered successfully')
